import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Tag, MessageSquare, Clock, AlertCircle, Trash2, AtSign, Send, AlertTriangle, UserPlus, FileText, Activity, Flag, Shield, ClipboardCopy, ExternalLink, FileCode, Terminal, Check, AlertOctagon } from 'lucide-react';
import StatusBadge from './StatusBadge';
import PriorityBadge from './PriorityBadge';
import { authenticatedFetch, generateCurlCommand } from '../utils/api';
import { useNotification } from '../contexts/NotificationContext';
import { useTeam } from '../contexts/TeamContext';
import Modal from './Modal';
import { LoadingSpinner } from './Loading';
import DeepScanModal from './DeepScanModal';

const highlightDomain = (text, domain) => {
  if (!text || !domain) return text;
  const lowerText = text.toLowerCase();
  const lowerDomain = domain.toLowerCase();
  const segments = [];
  let pos = 0;
  let idx = lowerText.indexOf(lowerDomain, pos);
  while (idx !== -1) {
    if (idx > pos) segments.push(<span key={`t${pos}`}>{text.slice(pos, idx)}</span>);
    segments.push(<mark key={`d${idx}`} className="bg-accent/25 text-accent rounded px-0.5">{text.slice(idx, idx + domain.length)}</mark>);
    pos = idx + domain.length;
    idx = lowerText.indexOf(lowerDomain, pos);
  }
  if (pos < text.length) segments.push(<span key={`t${pos}`}>{text.slice(pos)}</span>);
  return segments.length > 0 ? segments : text;
};

const CodeContextSection = React.memo(({ resultId, rawUrl, searchDomain }) => {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!resultId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    authenticatedFetch(`/results/${resultId}/code-context`)
      .then(data => {
        if (!cancelled) {
          if (data?.error) {
            setError(data.error);
          } else if (data?.lines) {
            setContext(data);
          } else {
            setError('No code context available');
          }
        }
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load code context');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [resultId]);

  const fileName = rawUrl
    ? rawUrl.replace(/.*github\.com\/[^/]+\/[^/]+\/blob\/[^/]+\//, '').replace(/#.*/, '')
    : context?.file_path || '';

  const lines = context?.lines || [];
  const domainContext = context?.domain_context;
  const hasDomainInPrimaryContext = Boolean(
    searchDomain &&
    lines.some((line) => (line.content || '').toLowerCase().includes(searchDomain.toLowerCase()))
  );
  const showDomainContext = Boolean(
    domainContext?.lines?.length &&
    (!hasDomainInPrimaryContext || domainContext?.line_number !== context?.line_number)
  );

  const renderLines = (contextLines, markerKey) => contextLines.map((line) => {
    const lineNum = line.num || line.number;
    const isMarked = line[markerKey];
    return (
      <div
        key={`${markerKey}-${lineNum}`}
        className={`flex ${isMarked ? (markerKey === 'is_secret' ? 'bg-red-500/15' : 'bg-accent/15') : 'hover:bg-white/5'}`}
      >
        <span className={`flex-shrink-0 w-10 text-right pr-3 select-none border-r border-white/5 mr-3 ${isMarked ? (markerKey === 'is_secret' ? 'text-red-400 font-semibold' : 'text-accent font-semibold') : 'text-ink-tertiary/50'}`}>
          {lineNum}
        </span>
        <code className={`whitespace-pre ${isMarked ? (markerKey === 'is_secret' ? 'text-red-300 font-medium' : 'text-accent font-medium') : 'text-gray-300'}`}>
          {line.content ? (searchDomain ? highlightDomain(line.content, searchDomain) : line.content) : '\u00A0'}
        </code>
      </div>
    );
  });

  if (loading) {
    return (
      <div className="space-y-2">
        <label className="text-sm text-ink-secondary flex items-center gap-1.5">
          <FileCode size={14} />
          Code Context
        </label>
        <div className="bg-surface-tertiary rounded-lg p-4 flex items-center justify-center">
          <LoadingSpinner size="sm" />
          <span className="ml-2 text-sm text-ink-secondary">Loading source code...</span>
        </div>
      </div>
    );
  }

  if (error || !context) {
    return (
      <div className="space-y-2">
        <label className="text-sm text-ink-secondary flex items-center gap-1.5">
          <FileCode size={14} />
          Code Context
        </label>
        <div className="bg-surface-tertiary rounded-lg p-3 text-sm text-ink-tertiary">
          {error || 'No code context available for this finding'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <label className="text-sm text-ink-secondary flex items-center gap-1.5">
            <FileCode size={14} />
            Code Context
          </label>
          {searchDomain && (
            <span className="text-xs bg-accent/10 text-accent px-2 py-0.5 rounded-full font-medium">
              {searchDomain}
            </span>
          )}
        </div>
        {fileName && (
          <span className="text-xs text-ink-tertiary font-mono truncate max-w-[300px]" title={fileName}>
            {fileName}
          </span>
        )}
      </div>
      <div className="bg-[#1a1b26] rounded-lg overflow-hidden border border-border">
        <div className="overflow-x-auto">
          <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 border-b border-white/5">
            Secret Context
          </div>
          <pre className="text-xs leading-5">
            {renderLines(lines, 'is_secret')}
          </pre>
          {showDomainContext && (
            <>
              <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-accent border-y border-accent/20 bg-accent/5">
                Domain Context {domainContext?.line_number ? `(line ${domainContext.line_number})` : ''}
              </div>
              <pre className="text-xs leading-5">
                {renderLines(domainContext.lines, 'is_domain')}
              </pre>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

const CurlVerifySection = React.memo(({ resultId }) => {
  const [curlData, setCurlData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    setCurlData(null);
    try {
      const data = await generateCurlCommand(resultId);
      if (data?.error) {
        setError(data.error);
      } else if (data?.curl_command) {
        setCurlData(data);
      } else {
        setError('No verification command could be generated');
      }
    } catch (err) {
      setError(err.message || 'Failed to generate verification command');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!curlData?.curl_command) return;
    navigator.clipboard.writeText(curlData.curl_command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-2">
      <label className="text-sm text-ink-secondary flex items-center gap-1.5">
        <Terminal size={14} />
        Quick Verify
      </label>

      {!curlData && !loading && !error && (
        <button
          onClick={handleGenerate}
          className="flex items-center gap-2 px-3 py-2 bg-surface-tertiary hover:bg-surface-tertiary/80 border border-border rounded-lg transition-colors text-sm text-ink"
        >
          <Terminal size={15} className="text-accent" />
          <span>Generate curl command</span>
        </button>
      )}

      {loading && (
        <div className="bg-surface-tertiary rounded-lg p-4 flex items-center justify-center">
          <LoadingSpinner size="sm" />
          <span className="ml-2 text-sm text-ink-secondary">Generating verification command...</span>
        </div>
      )}

      {error && (
        <div className="bg-danger-soft border border-danger/20 rounded-lg p-3 flex items-start gap-2">
          <AlertOctagon size={16} className="text-danger mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-danger">{error}</p>
            <button
              onClick={handleGenerate}
              className="mt-1 text-xs text-ink-secondary hover:text-ink underline"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {curlData && (
        <div className="space-y-2">
          <div className="bg-[#1a1b26] rounded-lg overflow-hidden border border-border">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/5">
              <span className="text-xs text-gray-500 font-mono">bash</span>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors px-2 py-0.5 rounded hover:bg-white/10"
              >
                {copied ? (
                  <>
                    <Check size={12} className="text-green-400" />
                    <span className="text-green-400">Copied</span>
                  </>
                ) : (
                  <>
                    <ClipboardCopy size={12} />
                    <span>Copy</span>
                  </>
                )}
              </button>
            </div>
            <div className="p-3 overflow-x-auto">
              <pre className="text-xs text-gray-300 whitespace-pre-wrap break-all font-mono leading-5">
                <span className="text-green-400 select-none">$ </span>{curlData.curl_command}
              </pre>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            {curlData.expected_success && (
              <div className="bg-success-soft/50 border border-success/10 rounded-lg p-2">
                <span className="text-success font-medium">If valid:</span>
                <p className="text-ink-secondary mt-0.5">{curlData.expected_success}</p>
              </div>
            )}
            {curlData.expected_failure && (
              <div className="bg-danger-soft/50 border border-danger/10 rounded-lg p-2">
                <span className="text-danger font-medium">If invalid:</span>
                <p className="text-ink-secondary mt-0.5">{curlData.expected_failure}</p>
              </div>
            )}
          </div>

          {curlData.warning && (
            <div className="bg-warning-soft/50 border border-warning/10 rounded-lg p-2 flex items-start gap-1.5">
              <AlertTriangle size={13} className="text-warning mt-0.5 shrink-0" />
              <p className="text-xs text-ink-secondary">{curlData.warning}</p>
            </div>
          )}

          <button
            onClick={handleGenerate}
            className="text-xs text-ink-tertiary hover:text-ink-secondary transition-colors"
          >
            Regenerate
          </button>
        </div>
      )}
    </div>
  );
});

const FindingDetailsPanel = ({ finding: initialFinding, onClose, onUpdate, searchDomain }) => {
  const { currentTeam, currentUser } = useTeam();
  const panelRef = useRef(null);
  const [localFinding, setLocalFinding] = useState({
    ...initialFinding,
    status: initialFinding?.status || 'new',
    priority: initialFinding?.priority || null,
    notes: initialFinding?.notes || ''
  });
  const [localNotes, setLocalNotes] = useState(initialFinding?.notes || '');
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [newTag, setNewTag] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { addSuccessNotification, addErrorNotification } = useNotification();
  const [tags, setTags] = useState(initialFinding?.tags || []);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [showMentionsList, setShowMentionsList] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const commentInputRef = useRef(null);
  const [mentionListIndex, setMentionListIndex] = useState(0);
  const [activities, setActivities] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [showReportFPModal, setShowReportFPModal] = useState(false);
  const [fpReason, setFpReason] = useState('');
  const [isSubmittingFP, setIsSubmittingFP] = useState(false);
  const [fpReportStatus, setFpReportStatus] = useState(null); // null, 'pending', 'approved', 'rejected'
  const [showDeepScanModal, setShowDeepScanModal] = useState(false);

  const statusOptions = [
    { value: 'new', label: 'New' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'false_positive', label: 'False Positive' },
    { value: 'wont_fix', label: "Won't Fix" }
  ];
  
  const priorityOptions = [
    { value: 'P1', label: 'P1' },
    { value: 'P2', label: 'P2' },
    { value: 'P3', label: 'P3' },
    { value: 'P4', label: 'P4' },
    { value: 'P5', label: 'P5' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDeleteModal) return;      
      if (panelRef.current && 
          !panelRef.current.contains(event.target) && 
          !event.target.closest('[role="dialog"]')) {
        onClose();
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, showDeleteModal]); 
  
  useEffect(() => {
    setLocalFinding(prev => ({
      ...prev,
      ...initialFinding,
      status: initialFinding?.status || prev.status,
      priority: initialFinding?.priority || prev.priority,
      notes: initialFinding?.notes || prev.notes
    }));
    setTags(initialFinding?.tags || []);
  }, [initialFinding]);

  const fetchComments = useCallback(async () => {
    if (!initialFinding?.finding_id) return;
    
    try {
      const response = await authenticatedFetch(`/findings/${initialFinding.finding_id}/comments`);
      if (response?.comments) {
        setComments(response.comments);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  }, [initialFinding?.finding_id]);

  const fetchTeamMembers = useCallback(async () => {
    if (!initialFinding?.finding_id || !currentTeam?.team_id) return;
    
    try {
      const response = await authenticatedFetch(`/teams/${currentTeam.team_id}/members`);
      if (response?.members) {
        setTeamMembers(response.members);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  }, [initialFinding?.finding_id, currentTeam?.team_id]);

  // Check if this result has been reported as false positive
  const checkFPReportStatus = useCallback(async () => {
    const resultId = initialFinding?.result_id;
    if (!resultId) return;
    
    try {
      const response = await authenticatedFetch(`/false-positive-reports/check/${resultId}`);
      if (response?.reported) {
        setFpReportStatus(response.report.status);
      } else {
        setFpReportStatus(null);
      }
    } catch (error) {
      console.error('Error checking FP report status:', error);
    }
  }, [initialFinding?.result_id]);

  useEffect(() => {
    if (initialFinding?.finding_id) {
      fetchComments();
      checkFPReportStatus();
      if (currentTeam?.team_id) {
        fetchTeamMembers();
      } else {
        setTeamMembers([]);
      }
    }
  }, [initialFinding?.finding_id, currentTeam?.team_id, fetchComments, fetchTeamMembers, checkFPReportStatus]);

  const handleNotesChange = (e) => {
    setLocalNotes(e.target.value);
  };

  const handleReportFalsePositive = async () => {
    if (!fpReason.trim() || fpReason.trim().length < 10) {
      addErrorNotification('Please provide a reason with at least 10 characters');
      return;
    }

    const resultId = initialFinding?.result_id;
    if (!resultId) {
      addErrorNotification('Unable to report: Missing result ID');
      return;
    }

    setIsSubmittingFP(true);
    try {
      const response = await authenticatedFetch('/false-positive-reports', {
        method: 'POST',
        body: JSON.stringify({
          result_id: resultId,
          reason: fpReason.trim()
        })
      });

      if (response?.success) {
        addSuccessNotification('False positive report submitted successfully. An admin will review it.');
        setShowReportFPModal(false);
        setFpReason('');
        setFpReportStatus('pending');
      } else {
        throw new Error(response?.message || 'Failed to submit report');
      }
    } catch (error) {
      addErrorNotification(error.message || 'Failed to submit false positive report');
    } finally {
      setIsSubmittingFP(false);
    }
  };

  const handleSaveNotes = async () => {
    setIsSaving(true);
    try {
      await handleUpdate({ 
        notes: localNotes,
        team_id: currentTeam?.team_id || null
      });
      setLocalFinding(prev => ({
        ...prev,
        notes: localNotes
      }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!localFinding?.finding_id) return;
    
    try {
      const response = await authenticatedFetch(`/findings/${localFinding.finding_id}`, {
        method: 'DELETE',
      });
  
      if (response?.success) {
        setShowDeleteModal(false);
        addSuccessNotification(response.message || 'Finding deleted successfully');
        if (onUpdate) {
          onUpdate();
        }
        onClose();
      } else {
        throw new Error(response?.message || 'Failed to delete finding');
      }
    } catch (error) {
      addErrorNotification(error.message || 'Failed to delete finding');
      console.error('Error deleting finding:', error);
    }
  };
  
  const handleUpdate = async (updates) => {
    try {
      const id = localFinding.finding_id || localFinding.result_id;
      if (!id) {
        console.error('No valid ID available', localFinding);
        addErrorNotification('Unable to update finding: Missing ID');
        return;
      }

      const response = await authenticatedFetch(`/findings/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...updates,
          team_id: currentTeam?.team_id || null
        })
      });

      if (response.error) {
        throw new Error(response.message || 'Failed to update finding');
      }

      setLocalFinding(prev => ({
        ...prev,
        ...updates,
        finding_id: response.finding_id || id
      }));
      
      if (onUpdate) {
        onUpdate(id, response);
      }

      addSuccessNotification('Finding updated successfully');
    } catch (error) {
      console.error('Error updating finding:', error);
      addErrorNotification('Failed to update finding');
    }
  };

  const handleAddTag = async () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      try {
        const response = await authenticatedFetch(`/findings/${localFinding.finding_id}/tags`, {
          method: 'POST',
          body: JSON.stringify({
            tag: newTag.trim(),
            team_id: currentTeam?.team_id || null
          })
        });

        if (response.success) {
          setTags([...tags, newTag.trim()]);
          setNewTag('');
          setShowTagInput(false);
          addSuccessNotification('Tag added successfully');
        }
      } catch (error) {
        console.error('Error adding tag:', error);
        addErrorNotification('Failed to add tag');
      }
    }
  };

  const handleRemoveTag = async (tagToRemove) => {
    try {
      const response = await authenticatedFetch(`/findings/${localFinding.finding_id}/tags/${encodeURIComponent(tagToRemove)}`, {
        method: 'DELETE',
        body: JSON.stringify({
          team_id: currentTeam?.team_id || null
        })
      });

      if (response.success) {
        setTags(tags.filter(tag => tag !== tagToRemove));
        addSuccessNotification('Tag removed successfully');
      }
    } catch (error) {
      console.error('Error removing tag:', error);
      addErrorNotification('Failed to remove tag');
    }
  };

  const highlightMentions = (text) => {
    return text.replace(/@(\w+)/g, '<span class="inline-flex items-center text-accent bg-accent-soft px-1 rounded">@$1</span>');
  };

  const handleCommentKeyDown = (e) => {
    if (e.key === '@') {
      setShowMentionsList(true);
      setMentionStartIndex(e.target.selectionStart);
      setMentionFilter('');
      setMentionListIndex(0);
    } else if (showMentionsList) {
      if (e.key === 'Escape') {
        setShowMentionsList(false);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionListIndex(prev => Math.min(prev + 1, filteredMembers.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionListIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && filteredMembers.length > 0) {
        e.preventDefault();
        handleMentionSelect(filteredMembers[mentionListIndex]);
      }
    }
  };

  const handleCommentChange = (e) => {
    setNewComment(e.target.value);
    if (showMentionsList) {
      const currentText = e.target.value.slice(mentionStartIndex + 1);
      setMentionFilter(currentText);
      if (!currentText.includes('@')) {
        setShowMentionsList(true);
      }
    }
  };

  const handleMentionSelect = (member) => {
    const beforeMention = newComment.slice(0, mentionStartIndex);
    const afterMention = newComment.slice(commentInputRef.current.selectionStart);
    setNewComment(`${beforeMention}@${member.username} ${afterMention}`);
    setShowMentionsList(false);
    commentInputRef.current.focus();
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !currentTeam?.team_id) return;

    try {
      const mentionRegex = /@(\w+)/g;
      const mentionMatches = [...newComment.matchAll(mentionRegex)];
      const mentions = mentionMatches
        .map(match => teamMembers.find(m => m.username === match[1])?.user_id)
        .filter(Boolean);

      const response = await authenticatedFetch(`/findings/${initialFinding.finding_id}/comments`, {
        method: 'POST',
        body: JSON.stringify({
          content: newComment,
          mentions,
          team_id: currentTeam.team_id
        }),
      });

      if (response) {
        setComments(prev => [...prev, response]);
        setNewComment('');
        addSuccessNotification('Comment added successfully');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      addErrorNotification(error.message || 'Failed to add comment');
    }
  };

  const filteredMembers = teamMembers.filter(member =>
    !mentionFilter || member.username.toLowerCase().includes(mentionFilter.toLowerCase())
  );

  const TabButton = ({ id, icon: Icon, label, count }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-sm transition-colors border-b-2 ${
        activeTab === id 
          ? 'border-accent text-ink font-medium' 
          : 'border-transparent text-ink-secondary hover:text-ink'
      }`}
    >
      <Icon size={15} />
      <span>{label}</span>
      {count > 0 && (
        <span className="ml-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-accent-soft text-accent leading-none">
          {count}
        </span>
      )}
    </button>
  );

  const handlePriorityChange = (newPriority) => {
    handleUpdate({ 
      priority: newPriority,
      team_id: currentTeam?.team_id || null
    });
  };

  const handleStatusChange = (newStatus) => {
    handleUpdate({ 
      status: newStatus,
      team_id: currentTeam?.team_id || null
    });
  };

  const handleAssigneeChange = (newAssignee) => {
    handleUpdate({ 
      assigned_to: newAssignee,
      team_id: currentTeam?.team_id || null
    });
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const response = await authenticatedFetch(
        `/findings/${initialFinding.finding_id}/comments/${commentId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      // Si la réponse est vide ou si elle indique un succès
      if (!response || response.success) {
        setComments(prev => prev.filter(c => c.comment_id !== commentId));
        addSuccessNotification('Comment deleted successfully');
      } else {
        throw new Error(response.message || 'Failed to delete comment');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      addErrorNotification(error.message || 'Failed to delete comment');
    }
  };

  const fetchActivities = useCallback(async () => {
    if (!initialFinding?.finding_id) return;
    
    setIsLoadingActivities(true);
    try {
      const response = await authenticatedFetch(`/findings/${initialFinding.finding_id}/activities`);
      if (response?.activities) {
        setActivities(response.activities);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setIsLoadingActivities(false);
    }
  }, [initialFinding?.finding_id]);

  useEffect(() => {
    if (initialFinding?.finding_id && activeTab === 'activity') {
      fetchActivities();
    }
  }, [initialFinding?.finding_id, activeTab, fetchActivities]);

  const getActivityIcon = (actionType) => {
    switch (actionType) {
      case 'status_change':
        return <AlertCircle size={16} className="text-accent" />;
      case 'priority_change':
        return <AlertTriangle size={16} className="text-warning" />;
      case 'assignee_change':
        return <UserPlus size={16} className="text-success" />;
      case 'note_update':
        return <FileText size={16} className="text-info" />;
      case 'tag_added':
      case 'tag_removed':
        return <Tag size={16} className="text-accent" />;
      case 'comment_added':
      case 'comment_deleted':
        return <MessageSquare size={16} className="text-warning" />;
      default:
        return <Activity size={16} className="text-ink-secondary" />;
    }
  };

  const getActivityMessage = (activity) => {
    const details = activity.details || {};
    
    switch (activity.action_type) {
      case 'status_change':
        return `Changed status from ${details.old_status || 'none'} to ${details.new_status}`;
      case 'priority_change':
        return `Changed priority from ${details.old_priority || 'none'} to ${details.new_priority}`;
      case 'assignee_change':
        return details.new_assignee 
          ? `Assigned to ${details.new_assignee_username}`
          : 'Removed assignee';
      case 'note_update':
        return 'Updated notes';
      case 'tag_added':
        return `Added tag "${details.tag}"`;
      case 'tag_removed':
        return `Removed tag "${details.tag}"`;
      case 'comment_added':
        return 'Added a comment';
      case 'comment_deleted':
        return 'Deleted a comment';
      default:
        return 'Unknown activity';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[9998]">
      <div ref={panelRef} className="fixed inset-y-0 right-0 w-[640px] bg-surface-secondary border-l border-border shadow-xl z-[9999] flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="text-base font-semibold text-ink">Finding Details</h3>
          <button 
            onClick={onClose} 
            className="text-ink-secondary hover:text-ink p-1.5 rounded-lg hover:bg-surface-tertiary transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Summary Card - Always visible */}
        <div className="px-4 py-2 border-b border-border">
          <div className="bg-surface-tertiary rounded-lg px-3 py-2">
            <div className="grid grid-cols-4 gap-3">
              <div>
                <span className="text-[11px] text-ink-tertiary uppercase tracking-wider">Date</span>
                <div className="text-xs text-ink mt-0.5">
                  {new Date(initialFinding?.timestamp * 1000).toLocaleString()}
                </div>
              </div>
              <div>
                <span className="text-[11px] text-ink-tertiary uppercase tracking-wider">Token Type</span>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className="text-xs text-ink truncate">{initialFinding?.details?.token_type}</span>
                  {localFinding?.priority && (
                    <PriorityBadge priority={localFinding.priority} size="xs" />
                  )}
                </div>
              </div>
              <div>
                <span className="text-[11px] text-ink-tertiary uppercase tracking-wider">Status</span>
                <div className="mt-0.5">
                  <StatusBadge status={localFinding?.status || 'new'} size="xs" />
                </div>
              </div>
              <div className="min-w-0">
                <span className="text-[11px] text-ink-tertiary uppercase tracking-wider">Repository</span>
                <div className="flex items-center gap-1 mt-0.5">
                  <a 
                    href={initialFinding?.details?.repository_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-accent hover:text-accent-hover truncate flex items-center gap-0.5"
                  >
                    {initialFinding?.details?.repository_url?.split('/').slice(-2).join('/')}
                    <ExternalLink size={10} className="shrink-0" />
                  </a>
                  {initialFinding?.details?.repository_url && (
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(initialFinding.details.repository_url);
                        addSuccessNotification('Repository URL copied to clipboard');
                      }}
                      className="p-0.5 hover:bg-surface-secondary rounded shrink-0"
                      title="Copy repository URL"
                    >
                      <ClipboardCopy size={11} className="text-ink-tertiary" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="px-4 py-2 border-b border-border flex items-center gap-2">
          {initialFinding?.details?.repository_url && (
            <button
              onClick={() => setShowDeepScanModal(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 bg-surface-tertiary text-ink hover:bg-surface-tertiary/80 border border-border rounded-lg transition-colors text-xs font-medium"
            >
              <Shield size={14} />
              <span>Deep Scan</span>
            </button>
          )}
          {fpReportStatus === 'pending' ? (
            <span className="px-2.5 py-1.5 rounded-lg bg-warning-soft text-warning text-xs flex items-center gap-1.5 font-medium">
              <Flag size={13} />
              <span>FP Pending</span>
            </span>
          ) : fpReportStatus === 'approved' ? (
            <span className="px-2.5 py-1.5 rounded-lg bg-success-soft text-success text-xs flex items-center gap-1.5 font-medium">
              <Flag size={13} />
              <span>Confirmed FP</span>
            </span>
          ) : fpReportStatus === 'rejected' ? (
            <span className="px-2.5 py-1.5 rounded-lg bg-danger-soft text-danger text-xs flex items-center gap-1.5 font-medium">
              <Flag size={13} />
              <span>FP Rejected</span>
            </span>
          ) : (
            <button
              onClick={() => setShowReportFPModal(true)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-ink-secondary hover:text-ink hover:bg-surface-tertiary rounded-lg transition-colors text-xs"
              title="Report as false positive"
            >
              <Flag size={13} />
              <span>Report FP</span>
            </button>
          )}
          <div className="flex-1" />
          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-danger hover:bg-danger-soft rounded-lg transition-colors text-xs"
            title="Delete finding"
          >
            <Trash2 size={13} />
            <span>Delete</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-4 border-b border-border">
          <TabButton id="details" icon={AlertCircle} label="Details" />
          <TabButton id="comments" icon={MessageSquare} label="Comments" count={comments.length} />
          <TabButton id="activity" icon={Clock} label="Activity" />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'details' && (
            <div className="p-4 space-y-6">
              {/* Code Context */}
              <CodeContextSection resultId={initialFinding?.result_id} rawUrl={initialFinding?.details?.raw_url} searchDomain={searchDomain} />

              {/* Quick Verify - LLM-generated curl command */}
              {initialFinding?.result_id && (
                <CurlVerifySection resultId={initialFinding.result_id} />
              )}

              {/* Triage Controls */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs text-ink-secondary font-medium">Status</label>
                  <select 
                    value={localFinding?.status || 'new'} 
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="w-full bg-surface-tertiary border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
                  >
                    {statusOptions.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-ink-secondary font-medium">Priority</label>
                  <select 
                    value={localFinding?.priority || ''} 
                    onChange={(e) => handlePriorityChange(e.target.value || null)}
                    className="w-full bg-surface-tertiary border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
                  >
                    <option value="">None</option>
                    {priorityOptions.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-ink-secondary font-medium">Assigned To</label>
                  {currentTeam?.team_id && teamMembers.length > 0 ? (
                    <select
                      value={localFinding?.assigned_to || ''}
                      onChange={(e) => handleAssigneeChange(e.target.value || null)}
                      className="w-full bg-surface-tertiary border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
                    >
                      <option value="">Unassigned</option>
                      {teamMembers.map(member => (
                        <option key={member.user_id} value={member.user_id}>
                          {member.username} ({member.role})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="w-full bg-surface-tertiary border border-border rounded-lg px-2 py-1.5 text-sm text-ink-tertiary italic">
                      No team
                    </div>
                  )}
                </div>
              </div>

              {/* Tags */}
              <div className="space-y-1.5">
                <label className="text-xs text-ink-secondary font-medium">Tags</label>
                <div className="flex flex-wrap gap-2">
                  {tags.map(tag => (
                    <span 
                      key={tag}
                      className="px-2 py-1 rounded-lg bg-surface-tertiary text-ink text-sm flex items-center gap-1"
                    >
                      {tag}
                      <button
                        onClick={() => handleRemoveTag(tag)}
                        className="hover:text-danger"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                  {showTagInput ? (
                    <div className="flex gap-1">
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                        className="px-2 py-1 bg-surface-tertiary border border-border rounded-lg text-ink text-sm"
                        placeholder="Enter tag..."
                        autoFocus
                      />
                      <button
                        onClick={() => setShowTagInput(false)}
                        className="text-ink-secondary hover:text-ink"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowTagInput(true)}
                      className="px-2 py-1 rounded-lg border border-dashed border-border text-ink-secondary hover:text-ink text-sm flex items-center gap-1"
                    >
                      <Tag size={14} />
                      Add Tag
                    </button>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-1.5">
                <label className="text-xs text-ink-secondary font-medium">Notes</label>
                <textarea
                  value={localNotes}
                  onChange={handleNotesChange}
                  placeholder="Add notes about this finding..."
                  className="w-full bg-surface-tertiary border border-border rounded-lg p-2 text-sm text-ink resize-vertical"
                  rows={3}
                />
                {localNotes !== localFinding.notes && (
                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveNotes}
                      disabled={isSaving}
                      className="px-3 py-1.5 bg-accent text-white text-xs font-medium rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
                    >
                      {isSaving ? (
                        <>
                          <LoadingSpinner size="sm" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        'Save Notes'
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="p-4">
              {isLoadingActivities ? (
                <div className="flex justify-center items-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : activities.length > 0 ? (
                <div className="relative">
                  {/* Ligne verticale continue */}
                  <div className="absolute left-[19px] top-6 bottom-6 w-0.5 bg-surface-tertiary/50"></div>
                  
                  <div className="space-y-6">
                    {activities.map((activity, index) => (
                      <div key={activity.activity_id} className="flex items-start gap-4 relative">
                        {/* Point et icône */}
                        <div className="relative">
                          {/* Cercle de fond */}
                          <div className="w-10 h-10 rounded-full bg-surface-secondary border border-border/50 shadow-lg absolute inset-0"></div>
                          {/* Point de connexion avec la ligne */}
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-surface-tertiary"></div>
                          {/* Icône au-dessus */}
                          <div className="w-10 h-10 flex items-center justify-center relative z-10">
                            {getActivityIcon(activity.action_type)}
                          </div>
                        </div>
                        
                        {/* Contenu */}
                        <div className="flex-1 bg-surface-secondary border border-border/50 rounded-lg p-3 relative">
                          {/* Triangle pointant vers l'icône */}
                          <div className="absolute left-[-6px] top-4 w-3 h-3 bg-surface-secondary border-l border-t border-border/50 transform rotate-45"></div>
                          
                          <div className="relative">
                            <p className="text-ink">{getActivityMessage(activity)}</p>
                            <div className="flex items-center gap-2 mt-1 text-sm text-ink-secondary">
                              <span className="font-medium text-ink">{activity.username}</span>
                              <span>•</span>
                              <span>{new Date(activity.created_at).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center text-ink-secondary py-8">
                  No activity found for this finding
                </div>
              )}
            </div>
          )}

          {activeTab === 'comments' && (
            <div className="p-4 space-y-4">
              {/* Liste des commentaires */}
              <div className="space-y-4">
                {comments.map(comment => (
                  <div key={comment.comment_id} className="bg-surface-tertiary rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-ink font-medium">{comment.username}</span>
                        <span className="text-ink-secondary text-sm">
                          {new Date(comment.created_at).toLocaleString()}
                        </span>
                      </div>
                      {comment.user_id === currentUser?.user_id && (
                        <button
                          onClick={() => handleDeleteComment(comment.comment_id)}
                          className="p-1.5 text-danger hover:text-danger hover:bg-danger-soft rounded transition-colors"
                          title="Delete comment"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                    <p 
                      className="text-ink whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: highlightMentions(comment.content) }}
                    />
                  </div>
                ))}
              </div>

              {/* Formulaire de commentaire - Montrer uniquement si dans une équipe */}
              {currentTeam?.team_id && teamMembers.length > 0 ? (
                <div className="relative">
                  <div className="flex gap-2">
                    <textarea
                      ref={commentInputRef}
                      value={newComment}
                      onChange={handleCommentChange}
                      onKeyDown={handleCommentKeyDown}
                      placeholder="Add a comment... Use @ to mention team members"
                      className="flex-1 bg-surface-tertiary border border-border rounded-lg p-2 text-ink resize-none"
                      rows={2}
                    />
                    <button
                      onClick={handleAddComment}
                      disabled={!newComment.trim()}
                      className="px-3 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send size={16} />
                    </button>
                  </div>

                  {/* Liste des mentions */}
                  {showMentionsList && (
                    <div className="absolute top-full left-0 w-64 mt-2 bg-surface-secondary border border-border rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                      {filteredMembers.length > 0 ? (
                        filteredMembers.map((member, index) => (
                          <button
                            key={member.user_id}
                            onClick={() => handleMentionSelect(member)}
                            className={`w-full px-3 py-2 text-left hover:bg-surface-tertiary text-ink flex items-center gap-2 ${
                              index === mentionListIndex ? 'bg-surface-tertiary/50' : ''
                            }`}
                          >
                            <div className="flex items-center gap-2 w-full">
                              <AtSign size={14} className="text-accent" />
                              <div className="flex flex-col">
                                <span className="font-medium">{member.username}</span>
                                <span className="text-xs text-ink-secondary">{member.role}</span>
                              </div>
                            </div>
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-ink-secondary">
                          No matching team members found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-ink-secondary py-4">
                  {currentTeam?.team_id ? 
                    "No team members available" : 
                    "You need to be in a team to comment and mention members"}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal de confirmation de suppression */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Confirm Deletion"
          className="z-[10000]"
        >
          <div className="space-y-4" role="dialog">
            <p className="text-ink">
              Are you sure you want to unflag this finding? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 rounded-lg bg-surface-tertiary text-ink hover:bg-surface-tertiary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-lg bg-danger text-white hover:bg-danger/90 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </Modal>

        {/* Modal for reporting false positive */}
        <Modal
          isOpen={showReportFPModal}
          onClose={() => {
            setShowReportFPModal(false);
            setFpReason('');
          }}
          title="Report as False Positive"
          className="z-[10000]"
        >
          <div className="space-y-4" role="dialog">
            <div className="bg-surface-tertiary rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-ink-secondary">Token Type:</span>
                <span className="text-ink font-medium">{initialFinding?.details?.token_type}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-ink-secondary">Repository:</span>
                <span className="text-ink truncate">{initialFinding?.details?.repository_url?.split('/').slice(-2).join('/')}</span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-ink-secondary">
                Why do you think this is a false positive? <span className="text-danger">*</span>
              </label>
              <textarea
                value={fpReason}
                onChange={(e) => setFpReason(e.target.value)}
                placeholder="e.g., This is a test token, a placeholder value, or a false match..."
                className="w-full bg-surface-tertiary border border-border rounded-lg p-3 text-ink resize-none focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                rows={4}
                maxLength={1000}
              />
              <div className="flex justify-between text-xs text-ink-tertiary">
                <span>Minimum 10 characters</span>
                <span>{fpReason.length}/1000</span>
              </div>
            </div>

            <div className="bg-info-soft border border-info/20 rounded-lg p-3">
              <p className="text-sm text-info">
                <AlertCircle size={14} className="inline mr-1.5 -mt-0.5" />
                Your report will be reviewed by an admin. If approved, it will help improve our detection patterns.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowReportFPModal(false);
                  setFpReason('');
                }}
                className="px-4 py-2 rounded-lg bg-surface-tertiary text-ink hover:bg-surface-tertiary/80 transition-colors"
                disabled={isSubmittingFP}
              >
                Cancel
              </button>
              <button
                onClick={handleReportFalsePositive}
                disabled={isSubmittingFP || fpReason.trim().length < 10}
                className="px-4 py-2 rounded-lg bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmittingFP ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Flag size={16} />
                    <span>Submit Report</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>

        {/* Deep Scan Modal */}
        <DeepScanModal
          isOpen={showDeepScanModal}
          onClose={() => setShowDeepScanModal(false)}
          repositoryUrl={initialFinding?.details?.repository_url}
          resultId={initialFinding?.result_id}
        />
      </div>
    </div>
  );
};

export default FindingDetailsPanel;